import { useState, useRef, useEffect } from "react";
import { Music, Volume2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * 迷你音乐播放器控件
 * 使用免版权示例音频营造旅行氛围
 */
export default function MusicPlayer() {
  const [playing, setPlaying] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    // 使用免版权轻音乐示例
    audioRef.current = new Audio("https://cdn.pixabay.com/download/audio/2022/05/27/audio_1808fbf07a.mp3?filename=soft-piano-100-bpm-121529.mp3");
    audioRef.current.loop = true;
    audioRef.current.volume = 0.3;

    return () => {
      audioRef.current?.pause();
      audioRef.current = null;
    };
  }, []);

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(() => {});
    }
    setPlaying(!playing);
  };

  return (
    <motion.div
      className="fixed bottom-6 right-6 z-50"
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ delay: 1.5, type: "spring", stiffness: 260, damping: 20 }}
    >
      <button
        onClick={toggle}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className="relative flex items-center justify-center rounded-full shadow-lg transition-all hover:scale-110"
        style={{
          width: 48,
          height: 48,
          backgroundColor: "oklch(0.97 0.015 70 / 0.92)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          boxShadow: "var(--ds-shadow-md)",
          color: "oklch(0.62 0.18 35)",
        }}
      >
        {playing ? (
          <span className="flex items-center" style={{ gap: 2 }}>
            <span className="w-0.5 h-3 bg-current rounded-full animate-pulse" />
            <span className="w-0.5 h-4 bg-current rounded-full animate-pulse" style={{ animationDelay: "0.1s" }} />
            <span className="w-0.5 h-2 bg-current rounded-full animate-pulse" style={{ animationDelay: "0.2s" }} />
          </span>
        ) : (
          <Music className="w-5 h-5" />
        )}

        <AnimatePresence>
          {showTooltip && (
            <motion.div
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              className="absolute right-14 whitespace-nowrap rounded-lg px-3 py-1.5 text-xs"
              style={{
                backgroundColor: "oklch(0.22 0.04 45 / 0.88)",
                color: "white",
                fontFamily: "var(--font-family)",
              }}
            >
              <span className="flex items-center" style={{ gap: 4 }}>
                <Volume2 className="w-3 h-3" />
                {playing ? "正在播放氛围音乐" : "点击播放氛围音乐"}
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </button>
    </motion.div>
  );
}
